import React, { useState, useEffect, useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import { sendError } from '../appsignal';

import EventPendingView from '../components/content/event/EventPendingView';
import BestOf from '../components/content/event/BestOf';
import EventOnlineView from '../components/content/event/EventOnlineView';
import PaymentMethodView from '../components/content/PaymentMethodView';
import PastEventInformation from '../components/content/event/PastEventInformation';
import Pricelist from '../components/content/event/Pricelist';

import fetchBestofConfig from '../utils/fetchBestofConfig';
import { HelpdeskWidgetContext } from '../components/content/HelpdeskWidget'

const PENDING_STATE = 'reminder';

const EventPage = (props) => {

  const { event } = props.viewer;

  const eventOnline = event.state !== PENDING_STATE;

  let initialSelectedTab;
  switch (true) {
    case event.searchByFaceAsFeature:
      initialSelectedTab = 3;
      break;
    default:
      initialSelectedTab = 1;
  }

  let pastEventNotificationHorizon = new Date();
  pastEventNotificationHorizon.setDate(pastEventNotificationHorizon.getDate() - 90);
  let currentEventDate = new Date(event.beginsAt);

  const [showPastEventNotification, setShowPastEventNotification] = useState(currentEventDate < pastEventNotificationHorizon);
  const [shouldExpandBestOf, setShouldExpandBestOf] = useState(props.match.params.bestof ? true : false);
  const [extendedSearchSelectedTab, setExtendedSearchSelectedTab] = useState(initialSelectedTab);
  const [extendedSearchExpand, setExtendedSearchExpand] = useState(false);
  const [bestOfAvailable, setBestOfAvailable] = useState(false);
  const [hlpDskState, hlpDskDispatch] = useContext(HelpdeskWidgetContext);

  useEffect(() => {
    hlpDskDispatch({ type: 'eventName', payload: event.name })
    hlpDskDispatch({ type: 'status', payload: event.state })
  }, [])

  React.useEffect(() => {
    fetchBestofConfig(event.sgId, 'HEAD')
      .then(config => {
        setBestOfAvailable(config.isAvailable);
      }).catch(error => {
        sendError(error);
      });
  })

  const handleClosePastEventNotification = () => {
    setShowPastEventNotification(false);
  };

  const handleExtendedSearchTabChanged = (event, value) => {
    setExtendedSearchExpand(true);
    setExtendedSearchSelectedTab(value);
  };

  const handleExtendedSearchExpand = () => {
    setExtendedSearchExpand(!extendedSearchExpand)
  };

  const handleExpandBestof = () => {
    setShouldExpandBestOf(true)
  };

  // In case a user navigates to an old event, we make a redirect to the archive website
  if (event.legacy) {
    window.location.href = `https://archive.sportograf.com/shop/event/${event.sgId}`;
    return null;
  }

  // In case a user navigates directly over the url, it is necessary to check if the
  // event has subevents. If so, forward him to the appropriate page.
  if (event.subEvents.length > 0) {
    return <Redirect to={`/event/${event.sgId}/subevents`} />;
  }

  return (
    <div className="container-960 container-page">
      {event.successorEvent && showPastEventNotification && (
        <PastEventInformation
          eventname={event.name}
          currentEventDate={event.beginsAt}
          successorEvent={event.successorEvent}
          onRequestClose={handleClosePastEventNotification}
        />
      )}
      {!eventOnline && (
        <EventPendingView
          pendingEvent={event}
          onExpandBestof={handleExpandBestof}
          bestOfAvailable={bestOfAvailable}
        />
      )}
      {eventOnline && (
        <EventOnlineView
          onlineEvent={event}
          onExpandBestof={handleExpandBestof}
          bestOfAvailable={bestOfAvailable}
          extendedSearchSelectedTab={extendedSearchSelectedTab}
          extendedSearchExpand={extendedSearchExpand}
          hasGpsSearchTab={event.gps}
          hasFaceSearchTab={event.searchByFaceAsFeature && !event.searchByFace}
          hasSmartSearchTab={false}
          hasExtendedSearch={!event.secondFactorType}
          onExtendedSearchTabChange={handleExtendedSearchTabChanged}
          onExtendedSearchExpand={handleExtendedSearchExpand}
        />
      )}
      {bestOfAvailable && (
        <BestOf
          sgId={event.sgId}
          expanded={!eventOnline}
          shouldExpandBestOf={shouldExpandBestOf}
        />
      )}
      {eventOnline && <Pricelist offer={event} />}
      <PaymentMethodView />
    </div>
  );

}

EventPage.propTypes = {
  match: PropTypes.object,
  viewer: PropTypes.object,
};

export { EventPage };

export default createFragmentContainer(EventPage, {
  viewer: graphql`
    fragment EventPage_viewer on Viewer {
      event(id: $eventId, language: $language) {
        id
        sgId
        beginsAt
        gps
	searchByFace
	searchByFaceAsFeature
        name
        legacy
        secondFactorType {
          type
        }
        state
        subEvents {
          id
        }
        successorEvent {
          ...PastEventInformation_successorEvent
        }
        ...EventPendingView_pendingEvent
        ...EventOnlineView_onlineEvent
        ...Pricelist_offer
      }
    }
  `,
});
